/**
 * Work Order Party Hooks (epic #763, #768)
 *
 * Consumes the Staff API role-based party endpoints under /work-orders/:id/parties.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';
import type { WorkOrderPartyRole, PartyType } from '@/lib/workOrderPartyRoles';

export interface WorkOrderParty {
  id: string;
  role: WorkOrderPartyRole;
  partyType: PartyType;
  personId: string | null;
  companyId: string | null;
  isPrimary: boolean;
  displayName: string | null;
  phone: string | null;
  email: string | null;
  businessId: string | null;
}

export interface SetPartyData {
  partyType: PartyType;
  personId?: string | null;
  companyId?: string | null;
  isPrimary?: boolean;
}

export function useWorkOrderParties(workOrderId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.workOrders.parties(workOrderId ?? ''),
    queryFn: () => api.get<{ data: WorkOrderParty[] }>(`/work-orders/${workOrderId}/parties`),
    enabled: !!workOrderId,
    staleTime: 60 * 1000,
  });
}

export function useSetWorkOrderParty() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      workOrderId,
      role,
      data,
    }: {
      workOrderId: string;
      role: WorkOrderPartyRole;
      data: SetPartyData;
    }) => api.put<WorkOrderParty>(`/work-orders/${workOrderId}/parties/${role}`, data),
    onSuccess: (_, { workOrderId }) => {
      // The detail key is a prefix of the parties key, so this refreshes both —
      // the work order's own `parties`/`customer` fields are now read straight
      // off the customer card, which must not show the previous orderer (#2105).
      void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.detail(workOrderId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.bookings.detail(workOrderId) });
    },
  });
}

export function useRemoveWorkOrderParty() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workOrderId, role }: { workOrderId: string; role: WorkOrderPartyRole }) =>
      api.delete(`/work-orders/${workOrderId}/parties/${role}`),
    onSuccess: (_, { workOrderId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.detail(workOrderId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.bookings.detail(workOrderId) });
    },
  });
}
